/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import { Check, X } from 'lucide-react';
import { cn } from '@/infrastructure/lib/utils';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '@/shared/utils/i18nHelper';

type CellFormatter = 'number' | 'date' | 'badge' | 'avatar' | 'user' | 'check';

interface TableColumnConfig {
  field: string;                    // key on each row object
  // header text (defaults to field). String legada ou objeto multilíngue
  // {en, pt} — resolvido via getLocalizedText antes de renderizar.
  label?: string | Record<string, string>;
  align?: 'left' | 'right' | 'center';
  formatter?: CellFormatter;
  // Largura CSS opcional aplicada ao <th> e <td>. Aceita qualquer valor
  // CSS válido (ex: '28%', '120px', '8rem'). Colunas sem width recebem
  // o espaço restante via table-layout auto. Útil para forçar comment
  // a expandir e user/date a ficarem compactos.
  width?: string;
  // For formatter='user': fields that build the cell content
  nameField?: string;               // default: 'full_name'
  emailField?: string;              // default: 'email'
  avatarField?: string;             // default: 'avatar_url'
}

interface TableChartProps {
  data: any[] | null | undefined;
  // Passthrough on top of the shared transform shape — table reads optional
  // `columns`, `excludeFields`, `showRank` and ignores xField/yField/etc.
  config: any;
}

function getInitials(name?: string | null, email?: string | null): string {
  const source = (name && name.trim()) || (email && email.split('@')[0]) || '?';
  const parts = source.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatPrimitive(value: any, formatter?: CellFormatter): string {
  if (value === null || value === undefined || value === '') return '—';
  if (formatter === 'number' && typeof value === 'number') {
    return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
  }
  if (formatter === 'date') {
    try { return new Date(value).toLocaleDateString(); } catch { return String(value); }
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function alignClass(align?: TableColumnConfig['align']): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return '';
}

export const TableChart = ({ data, config }: TableChartProps) => {
  const { t, i18n } = useTranslation();
  const rows: any[] = Array.isArray(data) ? data : [];
  const showRank = config?.showRank !== false; // default on
  const exclude = new Set<string>(config?.excludeFields ?? []);

  // Header de coluna: templates legados gravam `label` como string só-inglês.
  // Tentamos uma chave i18n por `field` (dashboards.columns.<field>); o
  // defaultValue preserva o label do template (objeto {en,pt} ou string crua).
  const headerLabel = (col: TableColumnConfig): string => {
    const fallback =
      col.label !== undefined && col.label !== null
        ? getLocalizedText(col.label, i18n.language)
        : col.field;
    return t(`dashboards.columns.${col.field}`, { defaultValue: fallback });
  };

  if (rows.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-muted-foreground">{t('dashboards.noData')}</p>
      </div>
    );
  }

  const columns: TableColumnConfig[] =
    config?.columns && config.columns.length > 0
      ? config.columns
      : Object.keys(rows[0])
          .filter((k) => !exclude.has(k))
          .map((field) => ({ field }));

  const renderCell = (row: any, col: TableColumnConfig) => {
    const value = row[col.field];

    if (col.formatter === 'user') {
      const name = row[col.nameField ?? 'full_name'] ?? value;
      const email = row[col.emailField ?? 'email'];
      const avatar = row[col.avatarField ?? 'avatar_url'];
      const initials = getInitials(name, email);
      return (
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="h-9 w-9 shrink-0">
            {avatar ? <AvatarImage src={avatar} alt={name || email || ''} /> : null}
            <AvatarFallback className="text-xs font-medium">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{name || '—'}</div>
            {email && name ? (
              <div className="text-xs text-muted-foreground truncate">{email}</div>
            ) : null}
          </div>
        </div>
      );
    }

    if (col.formatter === 'avatar') {
      const name = row[col.nameField ?? 'full_name'];
      const email = row[col.emailField ?? 'email'];
      return (
        <Avatar className="h-8 w-8">
          {value ? <AvatarImage src={value} alt={name || email || ''} /> : null}
          <AvatarFallback className="text-xs font-medium">
            {getInitials(name, email)}
          </AvatarFallback>
        </Avatar>
      );
    }

    if (col.formatter === 'badge') {
      return value !== null && value !== undefined && value !== ''
        ? <Badge variant="secondary" className="font-mono">{formatPrimitive(value)}</Badge>
        : <span className="text-muted-foreground">—</span>;
    }

    if (col.formatter === 'check') {
      const truthy = value === true || value === 'true' || value === 1 || value === '1';
      return truthy
        ? <Check className="h-4 w-4 text-emerald-500 inline-block" aria-label="yes" />
        : <X className="h-4 w-4 text-muted-foreground/60 inline-block" aria-label="no" />;
    }

    return (
      <span
        className={cn(
          col.formatter === 'number' ? 'tabular-nums font-medium' : 'whitespace-pre-wrap break-words'
        )}
      >
        {formatPrimitive(value, col.formatter)}
      </span>
    );
  };

  return (
    <div className="h-full w-full overflow-auto [scrollbar-width:thin] [scrollbar-color:hsl(var(--muted-foreground)/.4)_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/40 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/60 [&::-webkit-scrollbar-track]:bg-transparent">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {showRank && <TableHead className="w-10 text-center text-muted-foreground">#</TableHead>}
            {columns.map((col) => (
              <TableHead
                key={col.field}
                style={col.width ? { width: col.width } : undefined}
                className={cn(
                  'text-xs uppercase tracking-wide text-muted-foreground',
                  alignClass(col.align)
                )}
              >
                {headerLabel(col)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} className="hover:bg-muted/40">
              {showRank && (
                <TableCell className="text-center text-xs text-muted-foreground tabular-nums">
                  {i + 1}
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  style={col.width ? { width: col.width } : undefined}
                  className={cn('py-2.5', alignClass(col.align))}
                >
                  {renderCell(row, col)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
